<template>
  <!-- Damaged Package -->
  <DamagedPackage
    v-if="state.matches('reportProblemFlow.detailsStep.damagedPackage')"
    :isLoading
    :mostUrgentTask
    :uploadErrors
    @back="send('BACK')"
    @clear-errors="clearUploadErrors"
    @exit="send('EXIT')"
    @submit="
      (values) => declareProblem({ problemType: 'DAMAGED_PACKAGE', values })
    "
  />

  <!-- Out Of WarrantyItem -->
  <OutOfWarrantyItem
    v-if="state.matches('reportProblemFlow.detailsStep.oowItem')"
    :isLoading
    :mostUrgentTask
    :uploadErrors
    @back="send('BACK')"
    @clear-errors="clearUploadErrors"
    @exit="send('EXIT')"
    @submit="(values) => declareProblem({ problemType: 'OOW_ITEM', values })"
  />

  <!-- Item Not Received -->
  <ItemNotReceived
    v-if="state.matches('reportProblemFlow.detailsStep.itemNotReceived')"
    :isLoading
    :mostUrgentTask
    :uploadErrors
    @back="send('BACK')"
    @clear-errors="clearUploadErrors"
    @exit="send('EXIT')"
    @submit="
      (values) => declareProblem({ problemType: 'ITEM_NOT_RECEIVED', values })
    "
  />

  <!-- Account Locked -->
  <ExplainProblem
    v-if="state.matches('reportProblemFlow.detailsStep.accountLocked')"
    :isLoading
    :mostUrgentTask
    :subtitle="i18n(translations.accountLocked)"
    @back="send('BACK')"
    @exit="send('EXIT')"
    @submit="
      (values) => declareProblem({ problemType: 'ACCOUNT_LOCKED', values })
    "
  />

  <!-- Code Locked -->
  <ExplainProblem
    v-if="state.matches('reportProblemFlow.detailsStep.codeLocked')"
    :isLoading
    :mostUrgentTask
    :subtitle="i18n(translations.codeLocked)"
    @back="send('BACK')"
    @exit="send('EXIT')"
    @submit="(values) => declareProblem({ problemType: 'CODE_LOCKED', values })"
  />

  <!-- Missing Part -->
  <ExplainProblem
    v-if="state.matches('reportProblemFlow.detailsStep.missingPart')"
    :isLoading
    :mostUrgentTask
    :subtitle="i18n(translations.missingPart)"
    @back="send('BACK')"
    @exit="send('EXIT')"
    @submit="
      (values) => declareProblem({ problemType: 'MISSING_PART', values })
    "
  />

  <!-- Wrong Item -->
  <ExplainProblem
    v-if="state.matches('reportProblemFlow.detailsStep.wrongItem')"
    :isLoading
    :mostUrgentTask
    :subtitle="i18n(translations.wrongItem)"
    @back="send('BACK')"
    @exit="send('EXIT')"
    @submit="(values) => declareProblem({ problemType: 'WRONG_ITEM', values })"
  />

  <!-- Problem Not Identified -->
  <ExplainProblem
    v-if="state.matches('reportProblemFlow.detailsStep.problemNotIdentified')"
    :isLoading
    :mostUrgentTask
    :subtitle="i18n(translations.problemNotIdentified)"
    @back="send('BACK')"
    @exit="send('EXIT')"
    @submit="
      (values) =>
        declareProblem({ problemType: 'PROBLEM_NOT_IDENTIFIED', values })
    "
  />

  <!-- Other -->
  <ExplainProblem
    v-if="state.matches('reportProblemFlow.detailsStep.other')"
    :isLoading
    :mostUrgentTask
    :subtitle="i18n(translations.other)"
    @back="send('BACK')"
    @exit="send('EXIT')"
    @submit="(values) => declareProblem({ problemType: 'OTHER', values })"
  />
</template>

<script setup lang="ts">
import { useRuntimeConfig } from '#imports'
import { onBeforeUnmount, ref } from 'vue'

import { HttpApiError } from '@backmarket/http-api/src/utils/HttpApiError'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'

import type {
  AttachmentToApi,
  OrderlineOpenTask,
  ProblemType,
} from '~/api/seller-after-sales/types'
import { fetchAndGoToNextTask } from '~/scopes/customer-care/components/request.helpers'
import { useAttachmentUpload } from '~/scopes/customer-care/pages/request/components/Main/components/ActionBar/components/AttachmentsUpload/composables/useAttachmentUpload'
import {
  POLL_UPLOAD_CANCELLED,
  POLL_UPLOAD_FAILED,
} from '~/scopes/customer-care/pages/request/components/Main/components/ActionBar/components/AttachmentsUpload/composables/useAttachmentUpload.constants'
import { useOrderlinePublicIdFromRoute } from '~/scopes/customer-care/pages/request/composables/useOrderlinePublicIdFromRoute'

import type { MachineSend, MachineState } from '../../../ActionBar.machine.type'

import DamagedPackage from './DamagedPackage/DamagedPackage.vue'
import {
  HTTP_STATUS_CODE_BAD_REQUEST,
  HTTP_STATUS_CODE_UNPROCESSABLE_ENTITY,
  PROBLEM_ALREADY_DECLARED_TOAST_DURATION,
  PROBLEM_TYPES_EVIDENCE_REQUIRED,
} from './DetailsStep.constants'
import translations from './DetailsStep.translations'
import ItemNotReceived from './ItemNotReceived/ItemNotReceived.vue'
import OutOfWarrantyItem from './OutOfWarrantyItem/OutOfWarrantyItem.vue'
import ExplainProblem from './components/ExplainProblem/ExplainProblem.vue'

const { mostUrgentTask, state } = defineProps<{
  send: MachineSend
  state: MachineState
  mostUrgentTask?: OrderlineOpenTask
}>()
const { FF_ENABLE_CUSTOMER_CARE_REQUEST_NAVIGATION } = useRuntimeConfig().public

const i18n = useI18n()
const $httpFetch = useHttpFetch()
const { openErrorToast, openSuccessToast } = useTheToast()
const logger = useLogger()
const isLoading = ref(false)
const orderlinePublicId = useOrderlinePublicIdFromRoute()
const uploadErrors = ref<Array<Error>>([])
const abortController = ref<AbortController | null>(null)

const { uploadFilesAndGetMetadata } = useAttachmentUpload({
  orderlinePublicId: orderlinePublicId.value,
})

const emit = defineEmits(['done', 'exit'])

type EvidenceRequiredProblemType =
  (typeof PROBLEM_TYPES_EVIDENCE_REQUIRED)[number]

type ValuesWithEvidence = {
  message?: string
  attachments: File[]
  isNextTaskChecked: boolean
}

type ValuesWithoutEvidence = {
  message: string
  attachments?: AttachmentToApi[]
  isNextTaskChecked: boolean
}

type DeclareProblemArgs<T extends ProblemType> =
  T extends EvidenceRequiredProblemType
    ? { problemType: T; values: ValuesWithEvidence }
    : { problemType: T; values: ValuesWithoutEvidence }

const isProblemTypeEvidenceRequired = (
  problemType: ProblemType,
): problemType is EvidenceRequiredProblemType =>
  PROBLEM_TYPES_EVIDENCE_REQUIRED.includes(
    problemType as EvidenceRequiredProblemType,
  )

async function declareProblem<T extends ProblemType>({
  problemType,
  values,
}: DeclareProblemArgs<T>) {
  const isEvidenceRequired = isProblemTypeEvidenceRequired(problemType)

  abortController.value = new AbortController()

  try {
    isLoading.value = true

    let attachmentsToSend: AttachmentToApi[] = []
    if (isEvidenceRequired) {
      const uploadedAttachments = await uploadFilesAndGetMetadata(
        (values as ValuesWithEvidence)?.attachments ?? [],
        abortController.value?.signal,
      )
      attachmentsToSend = uploadedAttachments
    } else {
      attachmentsToSend = (values as ValuesWithoutEvidence)?.attachments ?? []
    }

    await $httpFetch(
      '/seller-after-sales/api/v1/orderlines/:orderlinePublicId/problem',
      {
        method: 'POST',
        pathParams: {
          orderlinePublicId: orderlinePublicId.value,
        },
        body: {
          message: values.message,
          problemType,
          attachments: attachmentsToSend,
        },
        signal: abortController.value?.signal,
      },
    )

    if (values.isNextTaskChecked && FF_ENABLE_CUSTOMER_CARE_REQUEST_NAVIGATION)
      fetchAndGoToNextTask(mostUrgentTask?.id).catch(() => {
        openErrorToast()
      })

    emit('done')
    openSuccessToast()
  } catch (err) {
    // Ignore abort/cancellation cases (no need to log or display feedback)
    if (
      err instanceof Error &&
      (err.name === 'AbortError' || err.message === POLL_UPLOAD_CANCELLED)
    ) {
      return
    }

    // Log the error
    const errorMessage = err instanceof Error ? err.message : String(err)
    const errorForLogging = err instanceof Error ? err : new Error(String(err))
    logger.error(errorMessage, {
      error: errorForLogging,
      owners: ['bot-squad-seller-care-front'],
    })

    // Handle Upload Service failure
    if (err instanceof Error && err.cause === POLL_UPLOAD_FAILED) {
      uploadErrors.value.push(err)

      return
    }

    // Sanitaze error to HttpApiError
    const apiError = HttpApiError.fromAnyError(err as Error)

    // BAD_REQUEST means the request to Intercom fails
    if (apiError.status === HTTP_STATUS_CODE_BAD_REQUEST) {
      uploadErrors.value = [new Error(i18n(translations.uploadFailed))]

      return
    }

    // UNPROCESSABLE_ENTITY means there's already a problem declared for this orderline
    if (apiError.status === HTTP_STATUS_CODE_UNPROCESSABLE_ENTITY) {
      openErrorToast({
        content: i18n(translations.problemAlreadyDeclared),
        duration: PROBLEM_ALREADY_DECLARED_TOAST_DURATION,
      })
      emit('exit')

      return
    }

    // Default case
    openErrorToast()
  } finally {
    isLoading.value = false
  }
}

function clearUploadErrors() {
  uploadErrors.value = []
}

onBeforeUnmount(() => {
  if (abortController.value) {
    abortController.value.abort()
  }
})
</script>
